/**
 * TBM 교육이 **어디까지 갔는가** — 상세 chrome의 「교육 중 · 2/3일」 배지와 회차 줄의 문구.
 *
 * ## 배경 (결정 2026-09-08 `tbm-education-progress-axis`)
 * 배포한 TBM 문서를 열어도 교육 진척을 알 수 없었다. 그 자리가 답하던 것은 「담당자가 아직
 * 교육을 진행하지 않았습니다」 한 줄, 곧 **세션 0건이냐**는 이진 사실뿐이라 3일짜리 교육의
 * 둘째 날에는 화면이 아무 말도 하지 않았다.
 *
 * ## 왜 판정이 여기 없는가
 * 판정은 **shared `deriveTbmEducationProgress` 한 벌**이 소유한다. 분자 축이 세션 개수가 아니라
 * **서로 다른 `sessionDate` 수**이고(같은 날 세션이 둘 생길 수도, 어제 행이 재사용될 수도 있어
 * 세션 수와 교육한 날 수는 양방향으로 어긋난다), 「완료」도 *모든 날 + 모든 세션 COMPLETED*라는
 * 보수적 판정이다 — 이런 축을 화면이 손으로 적으면 서버와 갈린다(메모리
 * `feedback-same-concept-judged-by-different-axes-in-ui-and-server`).
 *
 * 이 모듈이 갖는 것은 둘뿐이다: **`Report` → shared 입력 어댑트**와 **문구**.
 *
 * ## 왜 `lib/` 순수 모듈인가
 * 선례 `tbmConductorSignature`와 같은 이유다 — 컴포넌트 쪽 모듈에 두면 그 모듈을 `vi.mock`하는
 * 기존 spec들이 새 export를 몰라 `No "X" export is defined on the mock`으로 무더기로 죽는다
 * (vitest factory mock은 부분 mock이 아니다).
 */
import {
  deriveTbmEducationProgress,
  type TbmEducationProgress,
  type TbmEducationRound,
} from '@safetyhub/shared';
import type { Report } from './reports';

/**
 * 문서 데이터에서 교육 진행을 파생한다. 재료는 셋 — 교육 기간 두 값과 세션 행.
 *
 * - 세션이 없거나(0건) 행을 **모르면**(`undefined`, 목록·옛 응답) shared가 `null`을 준다.
 *   표시하지 않는다는 뜻이다: 그 자리는 이미 「담당자가 아직 교육을 진행하지 않았습니다」가
 *   말하고 있고, 두 줄이 같은 사실을 두 번 말하면 사용자는 둘이 다른 것을 뜻한다고 읽는다.
 * - 여기서 `educationSessionCount`로 다시 거르지 않는다 — 두 곳이 갈린다.
 *
 * @param now 만료 판정 시각. 화면은 넘기지 않고(지금이 곧 판정 시각이다) **spec만 주입**한다.
 */
export function resolveTbmEducationProgress(
  report: Pick<Report, 'educationStartDate' | 'educationEndDate' | 'educationSessions'>,
  now?: Date,
): TbmEducationProgress | null {
  return deriveTbmEducationProgress(
    {
      educationStartDate: report.educationStartDate,
      educationEndDate: report.educationEndDate,
      sessions: report.educationSessions,
    },
    now,
  );
}

/**
 * 상태 문구 한 벌 — 배지와 회차 줄이 같은 말을 쓴다(화면에 문자열을 직접 박지 말 것).
 *
 * `preparing`(F-55, 2026-09-13): 담당자가 「교육 시작」 전에 입력만 해서 회차가 lazy 생성된 상태.
 * 종전엔 이 상태도 「교육 중」이라 시작 전인데 진행 중으로 읽혔다(사용자 결정 2안 — 표시만 가른다).
 * `Record<state, …>`라 shared가 상태를 늘리면 여기 미기재를 tsc가 거부한다.
 *
 * `ended`(2026-09-30): 교육 기간이 끝났는데 완료가 아니다. 종전엔 「교육 중」이라 적혔는데, 기간이
 * 끝나면 새 회차를 열 수 없으므로 그 말은 **일어날 수 없는 일을 기다린다고 말하는 것**이었다.
 * 「종료」라 쓰고 「만료」라 쓰지 않는다 — 이 화면의 다른 자리(교육 화면의 안내문)가 이미
 * 「교육 기간이 종료되었습니다」라고 말하고 있어 같은 어휘여야 같은 사실로 읽힌다.
 *
 * ⚠️ 회차 줄(`TbmEducationRoundState`)은 배지 어휘의 **부분집합**이라 이 맵 한 벌이 양쪽을
 * 덮는다 — 회차는 `ended`를 가질 수 없다(기간은 문서 축이다).
 */
const STATE_LABEL: Record<TbmEducationProgress['state'], string> = {
  preparing: '교육 준비 중',
  'in-progress': '교육 중',
  completed: '교육 완료',
  ended: '교육 기간 종료',
};

/**
 * 배지 문구 — 「교육 중 · 2/3일」.
 *
 * 기간이 없으면(단일회차) 분수를 붙이지 않는다: 「1/1일」은 아무 정보도 더하지 않는다.
 * 기간 밖 세션도 분자에 들어가므로 「4/3일」이 나올 수 있고 **그대로 적는다** — 예정보다 많이
 * 진행한 것이 사실이고, 가리면 화면이 거짓말을 한다(결정 §1).
 */
export function tbmEducationProgressLabel(progress: TbmEducationProgress): string {
  const state = STATE_LABEL[progress.state];
  if (progress.totalDays === null) return state;
  return `${state} · ${progress.conductedDays}/${progress.totalDays}일`;
}

/**
 * 회차 줄 문구 — 「1회차 · 09-07 · 교육 완료」.
 *
 * ⚠️ 날짜는 **문자열 슬라이스**로 자른다(`date`는 이미 `'YYYY-MM-DD'`다). `new Date()`를 태우면
 * tz 이동으로 하루가 밀린다(레포 규약 `feedback-date-only-parsing`) — 교육을 한 날이 하루 전으로
 * 적히는 것은 기록으로서 틀린 값이다.
 */
export function tbmEducationRoundLabel(round: TbmEducationRound): string {
  return `${round.round}회차 · ${round.date.slice(5)} · ${STATE_LABEL[round.status]}`;
}
